import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { withTemplate } from "@/components/TemplatePicker";
import { XIcon, FilePdfIcon, FileXlsIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { getToken } from "@/lib/token";

const limpiar = (s) => String(s || "").replace(/[^\w-]+/g, "_").replace(/^_+|_+$/g, "");

async function descargar(url, nombre) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}`,
                                            "X-Company-Id": localStorage.getItem("agroport_company_id") || "",
                                            "X-Branch-Id": localStorage.getItem("agroport_branch_id") || "" } });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = (await res.json()).detail || msg; } catch { /* respuesta no JSON */ }
    throw new Error(msg);
  }
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = href; a.download = nombre;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10000);
}

/**
 * Selección de cuentas antes de exportar el estado de cuenta (CUENTA PRINCIPAL + SUBCUENTAS).
 * formato "pdf": un solo PDF con las cuentas elegidas o un PDF independiente por cuenta (.zip).
 * formato "excel": un archivo con las cuentas elegidas.
 */
export default function StatementExportModal({ clienteId, clienteCodigo, clienteNombre, formato = "pdf",
                                               periodo = {}, incluirPagadas = false, onClose }) {
  const [cuentas, setCuentas] = useState(null);
  const [sel, setSel] = useState([]);
  const [modo, setModo] = useState("conjunto");
  const [busy, setBusy] = useState(false);
  const filtros = () => {
    const p = new URLSearchParams();
    if (periodo.desde) p.set("desde", periodo.desde);
    if (periodo.hasta) p.set("hasta", periodo.hasta);
    if (incluirPagadas) p.set("incluir_pagadas", "true");
    return p;
  };
  useEffect(() => {
    api.get(`/reports/statements/${clienteId}/cuentas`, { params: Object.fromEntries(filtros()) })
      .then(({ data }) => { setCuentas(data || []); setSel((data || []).map((c) => c.clave)); })
      .catch((e) => { toast.error(apiErr(e)); setCuentas([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const todas = cuentas && sel.length === cuentas.length;
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const exportar = async () => {
    if (!sel.length) return toast.error("Selecciona al menos una cuenta");
    const orden = cuentas.filter((c) => sel.includes(c.clave)).map((c) => c.clave);   // principal primero
    const p = filtros();
    p.set("cuentas", orden.join(","));
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const base = `estado-cuenta-${limpiar(clienteCodigo || clienteNombre || clienteId)}`;
    setBusy(true);
    const t = toast.loading("Generando estado de cuenta…");
    try {
      if (formato === "excel") {
        await descargar(`${apiBase}/reports/statements/${clienteId}/excel?${p}`, `${base}.xlsx`);
      } else if (modo === "separados" && orden.length > 1) {
        await descargar(withTemplate(`${apiBase}/reports/statements/${clienteId}/pdf-separados?${p}`, "estado_cuenta"),
          `estados-de-cuenta-${limpiar(clienteCodigo || clienteId)}.zip`);
      } else {
        const unica = orden.length === 1 ? cuentas.find((c) => c.clave === orden[0]) : null;
        await descargar(withTemplate(`${apiBase}/reports/statements/${clienteId}/pdf?${p}`, "estado_cuenta"),
          `${base}${unica ? `-${limpiar(unica.etiqueta)}` : ""}.pdf`);
      }
      toast.success("Estado de cuenta descargado", { id: t });
      onClose();
    } catch (e) { toast.error(`No se pudo exportar: ${e.message}`, { id: t }); }
    finally { setBusy(false); }
  };

  const vm = useModalForm({ listo: cuentas !== null, onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="statement-export-modal">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg">Exportar estado de cuenta {formato === "excel" ? "(Excel)" : "(PDF)"}</h3>
            <p className="text-[11px] text-text-muted">{clienteNombre} · elige las cuentas a incluir</p>
          </div>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          {cuentas === null ? <div className="text-sm text-text-muted">Cargando cuentas…</div> : (
            <>
              <label className="flex items-center gap-2 text-sm font-medium border-b border-border/50 pb-2 cursor-pointer">
                <input type="checkbox" checked={Boolean(todas)} data-testid="cuentas-todas" className="accent-agro-green"
                  onChange={(e) => setSel(e.target.checked ? cuentas.map((c) => c.clave) : [])} />
                Seleccionar todas
              </label>
              <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="cuentas-lista">
                {cuentas.map((c) => (
                  <label key={c.clave} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-white/[0.03] cursor-pointer">
                    <input type="checkbox" checked={sel.includes(c.clave)} onChange={() => toggle(c.clave)} className="accent-agro-green"
                      data-testid={`cuenta-${c.clave}`} />
                    <span className="flex-1">{c.etiqueta}</span>
                    <span className="text-[11px] text-text-muted">{c.documentos} doc. · {fmtL(c.saldo)}</span>
                  </label>
                ))}
              </div>
              {sel.length === 0 && <div className="text-[11px] text-red-400">Selecciona al menos una cuenta para continuar.</div>}
              {formato === "pdf" && (
                <div className="border-t border-border/50 pt-3 space-y-1.5 text-sm" data-testid="export-modo">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="modo" checked={modo === "conjunto"} onChange={() => setModo("conjunto")} data-testid="modo-conjunto" />
                    Un solo PDF con las cuentas seleccionadas
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="modo" checked={modo === "separados"} onChange={() => setModo("separados")} data-testid="modo-separados" />
                    Un PDF independiente por cada cuenta <span className="text-[11px] text-text-muted">(se descargan juntos en un .zip)</span>
                  </label>
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={vm.cerrar} className="px-3 py-1.5 text-sm border border-border rounded text-text-secondary">Cancelar</button>
          <button onClick={exportar} disabled={busy || !cuentas || sel.length === 0} data-testid="export-confirmar"
            className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40 flex items-center gap-1.5">
            {formato === "excel" ? <FileXlsIcon size={14} /> : <FilePdfIcon size={14} />} {busy ? "Generando…" : "Exportar"}
          </button>
        </div>
      </div>
    </div>
  );
}
